import { lumi } from '@/lib/lumi'
import { notifyFormSubmission } from '@/lib/notifyFormSubmission'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { usePageMeta } from '@/hooks/usePageMeta'

// ─── Structure ────────────────────────────────────────────────────────────────

interface Question {
  id: string
  label: string
}

interface Block {
  title: string
  subtitle: string
  questions: Question[]
}

// Blocos 0-5 = perguntas, step 0 = identificação
const BLOCKS: Block[] = [
  {
    title: 'Bloco 0',
    subtitle: 'Sua experiência no Ciclo 1',
    questions: [
      { id: 'q1', label: 'O que mudou para você durante os 21 dias do Ciclo 1? O que você percebeu, sentiu ou viveu de diferente?' },
      { id: 'q2', label: 'Nos seus sonhos, nas suas emoções ou no seu corpo houve algo que chamou atenção durante esses 21 dias?' },
      { id: 'q3', label: 'Você sentiu emoção ao ler o mapa e escutar a meditação? Você sentiu alívio e libertação durante todo esse processo?' },
      { id: 'q4', label: 'Suas noites foram mais tranquilas para dormir? E ao acordar também sentia diferença?' },
    ],
  },
  {
    title: 'Bloco 1',
    subtitle: 'Infância e Programação Familiar',
    questions: [
      { id: 'q5',  label: 'Quando você era criança tinha algo que seus pais limitavam ou que algo era proibido para você sem que você entendesse?' },
      { id: 'q6',  label: 'Quando você era criança lembra de frases ou acontecimentos que lhe marcaram onde você foi proibido de se expressar?' },
      { id: 'q7',  label: 'Dentro da sua família quando você era criança sentia que era escutado e eram atendidos os seus desejos?' },
      { id: 'q8',  label: 'Teve algum acontecimento quando você era criança que marcou que você queria algo e não teve?' },
      { id: 'q9',  label: 'Quando você era criança você tinha medo de alguma situação? Exemplo, medo de alguém da sua família, parente, de dizer a verdade ou até de acontecer algum fato com você?' },
      { id: 'q10', label: 'Quem você era na família quando era criança: o que tinha regalias, o que tinha muitas restrições, era muito controlado, era muito protegido?' },
      { id: 'q11', label: 'Na sua infância você presenciou alguma situação de escassez de comida, dinheiro ou afeto?' },
      { id: 'q12', label: 'Na sua infância houve alguma doença grave na família, morte ou alguma situação marcante que abalou todos e a você?' },
      { id: 'q13', label: 'Você escutava quando era criança alguma frase que tinha que ficar calado, comportado, engolir o choro ou algo que foi profundamente reprimido?' },
      { id: 'q14', label: 'Na sua infância você teve que suprir de forma excessiva a ausência de alguém ou recebeu muitas responsabilidades que não dava conta?' },
      { id: 'q15', label: 'Você se sentia um filho amado pelos seus pais? O que seus pais exigiam ou esperavam muito de você?' },
      { id: 'q16', label: 'Você teve pais presentes na sua vida ou viveu a ausência de algum deles? Como essa presença ou ausência impactou quem você se tornou?' },
      { id: 'q17', label: 'Você viveu alguma situação de violência, abuso, assédio ou bullying em qualquer fase da sua vida, da infância até hoje? Se sim e se sentir confortável compartilhe o que ficou dessa experiência.' },
    ],
  },
  {
    title: 'Bloco 2',
    subtitle: 'Amor e Relacionamentos',
    questions: [
      { id: 'q18', label: 'Dentre as suas relações amorosas você passou por traições, humilhação, vergonha ou rejeição?' },
      { id: 'q19', label: 'Você sente verdadeiramente que merece o amor? Tem alguma frase que vem à mente sobre isso?' },
      { id: 'q20', label: 'Quando se trata de se relacionar você é a pessoa que confia ou desconfia mais?' },
      { id: 'q21', label: 'Você sente que precisa salvar alguém ou estar sempre na postura de priorizar alguém? Quem seria? E por quê?' },
    ],
  },
  {
    title: 'Bloco 3',
    subtitle: 'Crescimento e Prosperidade',
    questions: [
      { id: 'q22', label: 'O que você pensa sobre crescer e prosperar de forma ilimitada? Está totalmente decidido a fazer isso? Ou sente que há alguma limitação? Qual?' },
      { id: 'q23', label: 'Tem alguém na sua jornada que não acreditava em você? Se sim quem e por quê?' },
      { id: 'q24', label: 'Qual o maior sonho que você gostaria muito que acontecesse mas que de alguma forma ainda não se realizou?' },
      { id: 'q25', label: 'O que você gostaria muito que se realizasse que ainda não se realizou em sua vida?' },
      { id: 'q26', label: 'Quando você tem um trabalho a fazer você é a pessoa que faz tudo com vontade ou vive adiando projetos, trabalhos e sonhos?' },
    ],
  },
  {
    title: 'Bloco 4',
    subtitle: 'Padrões e Autossabotagem',
    questions: [
      { id: 'q27', label: 'Em qual área da vida você sente que repete mais padrões negativos?' },
      { id: 'q28', label: 'Dentro das suas memórias você sente que tem algo sempre se repetindo de forma negativa? O que é?' },
      { id: 'q29', label: 'Hoje você se sente preso a alguma situação que deseja sair e não consegue?' },
      { id: 'q30', label: 'Quando se trata de cuidar de você qual a sua postura: se prioriza sempre acima de qualquer coisa ou normalmente se deixa de lado? Compartilhe uma situação que já se repetiu.' },
      { id: 'q31', label: 'O que você vive adiando na sua vida mudar e ainda não criou coragem de fazer isso?' },
    ],
  },
  {
    title: 'Bloco 5',
    subtitle: 'Visão e Decisão',
    questions: [
      { id: 'q32', label: 'O que você pensa sobre avançar em todas as áreas da sua vida agora? Existe alguma área que sente resistência em priorizar? Qual?' },
      { id: 'q33', label: 'Se você pudesse mudar algo na sua vida agora sem nenhum obstáculo o que seria? O que te impede de fazer isso hoje?' },
    ],
  },
]

// step 0 = identificação, steps 1-6 = blocos de perguntas
const TOTAL_STEPS = 7 // 0..6
const TOTAL_BLOCKS = 6

const ALL_TEXT_IDS = BLOCKS.flatMap(b => b.questions.map(q => q.id))

// ─── Page ────────────────────────────────────────────────────────────────────

export default function PageFormularioCiclo2() {
  usePageMeta({
    title: 'Formulário Ciclo 2 — Protocolo de Liberação e Cocriação',
    description: 'Formulário de aprofundamento do Ciclo 2 do Protocolo de Liberação e Cocriação.',
    ogImage: 'https://static.lumi.new/76/768cd02825460eb1548b4156e8d0d987.svg',
    ogUrl: 'https://jornada.reacendaluz.com.br/formulario/ciclo2/',
  })

  const accentColor = '#B9A3D9'
  const accentSoft  = '#D4C3EA'

  const [step, setStep]           = useState(0)
  const [direction, setDirection] = useState(1)

  const [name, setName]       = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [email, setEmail]     = useState('')
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted]   = useState(false)
  const [error, setError]     = useState('')

  function updateAnswer(id: string, value: string) {
    setAnswers(prev => ({ ...prev, [id]: value }))
    setError('')
  }

  const progressBlock   = step === 0 ? 0 : step
  const progressPercent = (progressBlock / TOTAL_BLOCKS) * 100

  function handleContinue() {
    setError('')

    // Step 0: validação de identificação
    if (step === 0) {
      const trimName     = name.trim()
      const trimWhatsapp = whatsapp.trim()
      const trimEmail    = email.trim()
      if (!trimName) {
        setError('Por favor informe seu nome.')
        document.getElementById('field-name')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      if (!trimWhatsapp) {
        setError('Por favor informe seu WhatsApp.')
        document.getElementById('field-whatsapp')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      if (!trimEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimEmail)) {
        setError('Por favor informe um email válido.')
        document.getElementById('field-email')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      advance()
      return
    }

    // Steps 1-6: validação das perguntas do bloco
    if (step >= 1 && step <= TOTAL_BLOCKS) {
      const block = BLOCKS[step - 1]
      for (const q of block.questions) {
        if (!(answers[q.id] || '').trim()) {
          setError('Por favor responda todas as perguntas deste bloco antes de continuar.')
          document.getElementById(`field-${q.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          return
        }
      }
      if (step === TOTAL_BLOCKS) {
        handleSubmit()
      } else {
        advance()
      }
      return
    }
  }

  function advance() {
    setDirection(1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setStep(prev => prev + 1)
  }

  async function handleSubmit() {
    setSubmitting(true)
    try {
      const now = new Date().toISOString()
      const payload: Record<string, unknown> = {
        name:      name.trim(),
        whatsapp:  whatsapp.trim(),
        email:     email.trim(),
        createdAt: now,
        updatedAt: now,
      }
      for (const id of ALL_TEXT_IDS) {
        payload[id] = (answers[id] || '').trim()
      }
      await lumi.entities.formulario_ciclo2.create(payload)
      notifyFormSubmission({ name: name.trim(), formName: 'Ciclo 2', submittedAt: now })
      setSubmitted(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error('Erro ao enviar formulário Ciclo 2:', err)
      setError('Não foi possível enviar agora. Tente novamente em instantes.')
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Success ───────────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0f14] flex items-center justify-center px-5 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="max-w-xl w-full text-center"
        >
          <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-6" style={{ color: accentSoft }}>
            Protocolo de Liberação e Cocriação — Ciclo 2
          </p>

          <div
            className="w-16 h-16 rounded-full mx-auto mb-8 flex items-center justify-center"
            style={{ background: `${accentColor}18`, border: `1px solid ${accentSoft}55` }}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={accentSoft}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h1 className="font-display type-hero mb-6" style={{ color: 'var(--ink,#e8e0d5)' }}>
            Suas respostas{' '}
            <em className="italic font-light" style={{ color: accentSoft }}>
              chegaram
            </em>
          </h1>

          <p className="font-body type-body mb-5" style={{ color: 'rgba(232,224,213,0.75)' }}>
            Obrigada por compartilhar com tanta honestidade e profundidade.
          </p>
          <p className="font-body type-body mb-5" style={{ color: 'rgba(232,224,213,0.7)' }}>
            Lerei tudo com cuidado e presença e em até{' '}
            <span className="numerals">72</span> horas você receberá sua meditação e Mapa de Revelações do Ciclo{' '}
            <span className="numerals">2</span>.
          </p>
          <p className="font-body type-body italic" style={{ color: 'rgba(232,224,213,0.55)' }}>
            Com amor e presença, Mônica, Reacenda Luz
          </p>

          <div
            className="h-px w-24 mx-auto mt-12"
            style={{ background: `linear-gradient(90deg, transparent, ${accentSoft}66, transparent)` }}
          />
          <p className="text-[10px] tracking-[0.3em] uppercase font-body mt-8" style={{ color: 'rgba(232,224,213,0.45)' }}>
            Mônica — Reacenda Luz
          </p>
        </motion.div>
      </div>
    )
  }

  // ─── Form ──────────────────────────────────────────────────────────────────

  const isLastStep = step === TOTAL_BLOCKS

  return (
    <div className="min-h-screen bg-[#0a0f14]">
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-14 sm:py-20">

        {/* ── Header fixo ── */}
        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-10 sm:mb-14"
        >
          <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-5" style={{ color: accentSoft }}>
            Protocolo de Liberação e Cocriação
          </p>
          <h1 className="font-display type-hero mb-6" style={{ color: 'var(--ink,#e8e0d5)' }}>
            Ciclo{' '}
            <span className="numerals">2</span>
            {' '}—{' '}
            <em className="italic font-light" style={{ color: accentSoft }}>
              Indo mais fundo
            </em>
          </h1>
          <p className="font-body type-body" style={{ color: 'rgba(232,224,213,0.7)', lineHeight: 1.8 }}>
            Você chegou ao Ciclo 2. Esse é o momento de ir mais fundo.
            Responda com honestidade e profundidade.
            Suas respostas me ajudam a criar a meditação e o Mapa de Revelações específicos para essa camada da sua jornada.
          </p>
          <div
            className="h-px w-24 mx-auto mt-10"
            style={{ background: `linear-gradient(90deg, transparent, ${accentSoft}66, transparent)` }}
          />
        </motion.header>

        {/* ── Barra de progresso ── */}
        {step > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] tracking-[0.3em] uppercase font-body" style={{ color: accentSoft }}>
                Bloco{' '}
                <span className="numerals">{progressBlock}</span>
                {' '}de{' '}
                <span className="numerals">{TOTAL_BLOCKS}</span>
              </p>
              <p className="text-[11px] font-body numerals" style={{ color: 'rgba(232,224,213,0.4)' }}>
                {Math.round(progressPercent)}%
              </p>
            </div>

            <div
              className="w-full h-[2px] rounded-full overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.07)' }}
            >
              <motion.div
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, ${accentColor}, ${accentSoft})` }}
                initial={{ width: `${((progressBlock - 1) / TOTAL_BLOCKS) * 100}%` }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              />
            </div>

            <div className="flex justify-between mt-2">
              {Array.from({ length: TOTAL_BLOCKS }).map((_, i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                  style={{
                    background: i < progressBlock ? accentColor : 'rgba(255,255,255,0.15)',
                  }}
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* ── Conteúdo do step (animado) ── */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={{
              enter: (d: number) => ({ opacity: 0, x: d > 0 ? 40 : -40 }),
              center: { opacity: 1, x: 0 },
              exit:  (d: number) => ({ opacity: 0, x: d > 0 ? -40 : 40 }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.45, ease: 'easeInOut' }}
          >

            {/* STEP 0 — Identificação */}
            {step === 0 && (
              <section className="space-y-5">
                <div className="mb-8">
                  <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-3" style={{ color: accentSoft }}>
                    Antes de começar
                  </p>
                  <h2 className="font-display type-subtitle" style={{ color: 'var(--ink,#e8e0d5)' }}>
                    <em className="italic font-light">Quem é você?</em>
                  </h2>
                  <div
                    className="h-px w-16 mt-5"
                    style={{ background: `linear-gradient(90deg, ${accentSoft}55, transparent)` }}
                  />
                </div>

                <TextInput
                  id="field-name"
                  label="Seu nome completo"
                  value={name}
                  onChange={v => { setName(v); setError('') }}
                  accentSoft={accentSoft}
                />
                <TextInput
                  id="field-whatsapp"
                  label="Seu WhatsApp com DDD"
                  value={whatsapp}
                  onChange={v => { setWhatsapp(v); setError('') }}
                  accentSoft={accentSoft}
                />
                <TextInput
                  id="field-email"
                  label="Seu email"
                  type="email"
                  value={email}
                  onChange={v => { setEmail(v); setError('') }}
                  accentSoft={accentSoft}
                />
              </section>
            )}

            {/* STEPS 1-6 — Blocos de perguntas */}
            {step >= 1 && step <= TOTAL_BLOCKS && (() => {
              const block  = BLOCKS[step - 1]
              const offset = BLOCKS.slice(0, step - 1).reduce((s, b) => s + b.questions.length, 0)
              return (
                <section>
                  <div className="mb-10 text-center sm:text-left">
                    <p
                      className="text-[10px] tracking-[0.4em] uppercase font-body mb-2 numerals"
                      style={{ color: accentSoft }}
                    >
                      {block.title}
                    </p>
                    <h2 className="font-display type-subtitle" style={{ color: 'var(--ink,#e8e0d5)' }}>
                      <em className="italic font-light">{block.subtitle}</em>
                    </h2>
                    <div
                      className="h-px w-16 mt-5 mx-auto sm:mx-0"
                      style={{ background: `linear-gradient(90deg, ${accentSoft}55, transparent)` }}
                    />
                  </div>

                  <div className="space-y-8">
                    {block.questions.map((q, qIdx) => (
                      <QuestionField
                        key={q.id}
                        id={q.id}
                        number={offset + qIdx + 1}
                        label={q.label}
                        value={answers[q.id] || ''}
                        onChange={v => updateAnswer(q.id, v)}
                        accentSoft={accentSoft}
                      />
                    ))}
                  </div>
                </section>
              )
            })()}

          </motion.div>
        </AnimatePresence>

        {/* ── Erro + Botão de ação ── */}
        <div className="mt-10 pt-4">
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[13px] font-body text-center mb-4"
              style={{ color: '#f87171' }}
            >
              {error}
            </motion.p>
          )}
          <button
            type="button"
            onClick={handleContinue}
            disabled={submitting}
            className="block w-full text-center btn-primary px-4 sm:px-8 py-5 min-h-[56px] rounded-sm type-button tracking-[0.12em] sm:tracking-[0.18em] uppercase disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting
              ? 'Enviando...'
              : isLastStep
                ? 'Enviar minhas respostas'
                : 'Continuar'}
          </button>

          {isLastStep && (
            <p className="text-[11px] font-body text-center mt-6 italic" style={{ color: 'rgba(232,224,213,0.45)' }}>
              Suas respostas são tratadas com total sigilo.
            </p>
          )}
        </div>

        {/* ── Footer ── */}
        <footer className="mt-16 text-center">
          <div
            className="h-px w-24 mx-auto mb-6"
            style={{ background: `linear-gradient(90deg, transparent, ${accentSoft}44, transparent)` }}
          />
          <p className="text-[10px] tracking-[0.3em] uppercase font-body" style={{ color: 'rgba(232,224,213,0.45)' }}>
            Reacenda Luz · Mônica Maria
          </p>
        </footer>

      </div>
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function TextInput({
  id, label, value, onChange, accentSoft, type = 'text',
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  accentSoft: string
  type?: string
}) {
  return (
    <div id={id}>
      <label
        className="block text-[10px] tracking-[0.3em] uppercase font-body mb-2"
        style={{ color: accentSoft }}
      >
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="input-field w-full rounded-sm px-4 py-3 font-body text-[16px]"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          color: 'var(--ink,#e8e0d5)',
        }}
      />
    </div>
  )
}

function QuestionField({
  id, number, label, value, onChange, accentSoft,
}: {
  id: string
  number: number
  label: string
  value: string
  onChange: (v: string) => void
  accentSoft: string
}) {
  return (
    <div id={`field-${id}`}>
      <div className="flex items-baseline gap-3 mb-3">
        <span className="numerals text-[12px] tracking-[0.2em] font-body flex-shrink-0" style={{ color: accentSoft }}>
          {String(number).padStart(2, '0')}
        </span>
        <label
          htmlFor={id}
          className="font-body type-body"
          style={{ color: 'var(--ink,#e8e0d5)', lineHeight: 1.6 }}
        >
          {label}
        </label>
      </div>
      <textarea
        id={id}
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={5}
        className="input-field w-full rounded-sm px-4 py-3 font-body text-[16px] resize-y"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          color: 'var(--ink,#e8e0d5)',
          lineHeight: 1.7,
          minHeight: 120,
        }}
        placeholder="Escreva sua resposta aqui..."
      />
    </div>
  )
}
